// Vendor
import PropTypes from 'prop-types';
import React, { memo, useEffect, useReducer, useState } from 'react';
import { RegisterOptions, useFormContext, useWatch } from 'react-hook-form';

// Reducer
import { DDownMultiSelectReducer, initialState } from './reducer';

// Components
import { Checkbox, CheckboxType } from 'src/components/atoms/Checkbox';
import { DropdownButton, DropdownButtonSize } from 'src/components/atoms/DropdownButton';
import Text, { TextColor, TextVariant } from 'src/components/atoms/Text';
import OutsideDetector from '../OutsideDetector/OutsideDetector';
import { ChoicesBox } from './components/ChoicesBox';
import { SearchBox } from './components/SearchBox';
import {
  Container,
  HelperText,
  OptionContainer,
  OptionsContainer,
  SearchBoxContainer,
  StateMessage
} from './styles';

// Helper
import { generateUUID } from 'src/helpers/generateUUID';

export type DropdownMultiSelectProps = {
  data: CheckboxType[];
  onApply?: () => void;
  onChange?: ({
    ids,
    data,
    isCheckAll,
    q
  }: {
    ids: string;
    data: CheckboxType[];
    isCheckAll: boolean;
    q?: string;
  }) => void;
  name: string;
  totalRows?: number;
  isDisabled?: boolean;
  isLoading?: boolean;
  showSearch?: boolean;
  onSearch?: (q: string) => void;
  placeholder?: string;
  size?: DropdownButtonSize;
  config?: RegisterOptions;
  textSelectAll: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemRef?: any;
};

export const DropdownMultiSelect: React.FC<DropdownMultiSelectProps> = (props) => {
  const {
    data,
    onChange,
    onApply,
    name,
    totalRows,
    isLoading = false,
    placeholder,
    size,
    config,
    itemRef,
    isDisabled,
    onSearch,
    showSearch = false,
    textSelectAll
  } = props;

  if (showSearch && !onSearch) {
    throw new Error('You must use the props showSearch, onSearch to enable the search-box');
  }

  const [{ items, q, itemsSelected, checkAll }, dispatch] = useReducer(
    DDownMultiSelectReducer,
    initialState
  );

  const {
    register,
    control,
    setValue,
    formState: { errors },
    clearErrors
  } = useFormContext();

  useWatch({
    control,
    name
  });

  const [isOpen, setIsOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    if (data) {
      dispatch({
        type: 'load',
        payload: { items: data, q }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    register(name, config);
  }, [config, name, register]);

  useEffect(() => {
    if (isChecked) {
      const dataString = checkAll
        ? ''
        : itemsSelected.map((item: CheckboxType) => item.value).join(',');
      onChange?.({ ids: dataString, data: itemsSelected, isCheckAll: Boolean(checkAll) });
      setIsChecked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChecked]);

  const onChanging = (check: CheckboxType) => {
    dispatch({ type: 'selected', payload: { item: check } });

    setValue(name, check.value);
    setIsChecked(true);
  };

  const removeAll = () => {
    dispatch({ type: 'removeAll' });
    setIsChecked(true);
  };

  const onItemSelected = (item: CheckboxType) => {
    clearErrors(name);
    onChanging(item);
  };

  const getError = () => {
    const message = errors[name]?.message;
    return message !== '' ? message : config?.required;
  };

  const hasError = Object.prototype.hasOwnProperty.call(errors, name);

  const Placeholder = () => {
    const selectedCount = itemsSelected.length;
    const propertyText = selectedCount === 1 ? 'Property' : 'Properties';

    return (
      <Text color={TextColor.primary} variant={TextVariant.small}>
        {(selectedCount === data.length && !q) || selectedCount === 0
          ? textSelectAll
          : `(${selectedCount}) ${propertyText}`}
      </Text>
    );
  };

  const Item = ({
    value,
    label,
    isChecked
  }: {
    value: string | null;
    label: string;
    isChecked: boolean;
  }) => {
    const valueItem = value ? value.toString() : generateUUID();
    return (
      <Checkbox
        isChecked={value ? isChecked : false}
        isDisabled={value === null}
        value={valueItem}
        label={label}
        name={valueItem}
        onClick={onItemSelected}
      />
    );
  };

  const errorLabel = hasError && getError();

  const hasResult = items.some((item) => item.isVisible);

  const setPropertyFilter = () => {
    setIsOpen(false);
    onApply?.();
  };

  return (
    <OutsideDetector onClickOutside={setPropertyFilter}>
      <Container role="dropdownMultiSelect" data-populated={itemsSelected.length > 0}>
        <DropdownButton
          {...props}
          showLabel
          hasError={!isDisabled && hasError}
          isOpen={isOpen}
          label={placeholder}
          onOpened={() => setIsOpen(false)}
          onClosed={() => setIsOpen(true)}
          size={size}
        >
          {Placeholder()}
        </DropdownButton>

        {isOpen && !isDisabled && (
          <OptionContainer rows={items.length}>
            {showSearch && onSearch && (
              <SearchBoxContainer>
                <SearchBox
                  name={name}
                  data={data}
                  defaultValue={q}
                  onSearch={(q) => {
                    dispatch({ type: 'search', payload: { q } });
                    onSearch(q);
                  }}
                />
                <ChoicesBox
                  name={name}
                  data={data}
                  totalRows={totalRows}
                  isLoading={isLoading}
                  dataSelected={itemsSelected}
                  removeAll={removeAll}
                  onSelected={(check) => {
                    dispatch({ type: 'selectAll', payload: { check } });

                    if (q) {
                      const selectedItems = items
                        .filter((item) => check && item.isVisible)
                        .map((item) => ({ ...item, isChecked: true }));

                      const isCheckAll = data.length === selectedItems.length;

                      const dataString = isCheckAll
                        ? ''
                        : selectedItems.map((item) => item.value).join(',');
                      onChange?.({
                        ids: dataString,
                        data: selectedItems,
                        isCheckAll
                      });
                    } else {
                      onChange?.({ ids: '', data: [], isCheckAll: check });
                    }
                  }}
                />
              </SearchBoxContainer>
            )}
            {!hasResult && <StateMessage>No options found</StateMessage>}
            {hasResult && (
              <OptionsContainer rows={items.length}>
                <ul>
                  {items?.map(({ value, label, isVisible }, index) => {
                    if (!isVisible) return null;

                    const checked =
                      itemsSelected.find((item: CheckboxType) => item.value === value)?.isChecked ||
                      false;
                    const config = items.length === index + 1 ? { ref: itemRef } : null;
                    const selected = checked ? 'selected' : '';

                    return (
                      <li {...config} key={value} className={`${selected}`}>
                        <Item value={value} label={label} isChecked={checked} />
                      </li>
                    );
                  })}
                </ul>
              </OptionsContainer>
            )}
          </OptionContainer>
        )}

        {typeof errorLabel === 'string' && !isDisabled && (
          <HelperText>
            <Text variant={TextVariant.small} color={TextColor.error}>
              <>{getError()}</>
            </Text>
          </HelperText>
        )}
      </Container>
    </OutsideDetector>
  );
};

DropdownMultiSelect.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      isChecked: PropTypes.bool.isRequired,
      isDisabled: PropTypes.bool
    }).isRequired
  ).isRequired,
  name: PropTypes.string.isRequired,
  totalRows: PropTypes.number,
  placeholder: PropTypes.string,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  showSearch: PropTypes.bool,
  onSearch: PropTypes.func,
  textSelectAll: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onApply: PropTypes.func,
  size: PropTypes.oneOf(['sm', 'md'] as const)
};

DropdownMultiSelect.defaultProps = {
  name: 'DropdownMultiSelect',
  size: 'md'
};

const AdapterDropdown = React.forwardRef((props: DropdownMultiSelectProps, ref) => (
  <DropdownMultiSelect {...props} />
));

export default memo(AdapterDropdown);
